import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalHospital as HospitalIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import adminApiService, { User, Institution, CreateUserRequest, CreateInstitutionRequest } from '../services/adminApiService';

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [institutionDialogOpen, setInstitutionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Form state for creating/editing users
  const [userFormData, setUserFormData] = useState({
    email: '',
    phone: '',
    password: '',
    role: 'patient' as User['role'],
    first_name: '',
    last_name: '',
    date_of_birth: '',
    national_id: '',
    gender: '',
    address: '',
    city: '',
    institution_id: '',
    specialization: '',
    license_number: ''
  });

  // Form state for creating/editing institutions
  const [institutionFormData, setInstitutionFormData] = useState({
    name: '',
    type: 'hospital' as Institution['type'],
    address: '',
    region: '',
    comuna: '',
    phone: '',
    email: '',
    rut: '',
    codigo_deis: '',
    capacity: 0,
    specialties: [] as string[]
  });

  // Fetch data with React Query
  const { data: users = [], isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApiService.getUsers(),
    select: (data) => data.data,
  });

  const { data: institutions = [], isLoading: institutionsLoading } = useQuery({
    queryKey: ['admin-institutions'],
    queryFn: () => adminApiService.getInstitutions(),
  });

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminApiService.getStats(),
  });

  // Mutations for CRUD operations
  const createUserMutation = useMutation({
    mutationFn: (userData: CreateUserRequest) => adminApiService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      setUserDialogOpen(false);
      setSnackbarMessage('Usuario creado exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      resetUserForm();
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Error al crear usuario');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: Partial<User> }) => 
      adminApiService.updateUser(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setUserDialogOpen(false);
      setSnackbarMessage('Usuario actualizado exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      resetUserForm();
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Error al actualizar usuario');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => adminApiService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      setSnackbarMessage('Usuario eliminado exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Error al eliminar usuario');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const createInstitutionMutation = useMutation({
    mutationFn: (institutionData: CreateInstitutionRequest) => 
      adminApiService.createInstitution(institutionData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-institutions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      setInstitutionDialogOpen(false);
      setSnackbarMessage('Institución creada exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      resetInstitutionForm();
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Error al crear institución');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  const deleteInstitutionMutation = useMutation({
    mutationFn: (institutionId: string) => adminApiService.deleteInstitution(institutionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-institutions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      setSnackbarMessage('Institución eliminada exitosamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Error al eliminar institución');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    },
  });

  // Helper functions
  const resetUserForm = () => {
    setUserFormData({
      email: '',
      phone: '',
      password: '',
      role: 'patient',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      national_id: '',
      gender: '',
      address: '',
      city: '',
      institution_id: '',
      specialization: '',
      license_number: ''
    });
    setSelectedUser(null);
  };

  const resetInstitutionForm = () => {
    setInstitutionFormData({
      name: '',
      type: 'hospital',
      address: '',
      region: '',
      comuna: '',
      phone: '',
      email: '',
      rut: '',
      codigo_deis: '',
      capacity: 0,
      specialties: []
    });
    setSelectedInstitution(null);
  };

  const handleCreateUser = () => {
    resetUserForm();
    setUserDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUserFormData({
      email: user.email,
      phone: user.phone,
      password: '', // Don't populate password for editing
      role: user.role,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      date_of_birth: '', // Would need to be provided by API
      national_id: '', // Would need to be provided by API
      gender: '',
      address: '',
      city: '',
      institution_id: user.institutionId || '',
      specialization: user.specialization || '',
      license_number: user.licenseNumber || ''
    });
    setUserDialogOpen(true);
  };

  const handleCreateInstitution = () => {
    resetInstitutionForm();
    setInstitutionDialogOpen(true);
  };

  const handleEditInstitution = (institution: Institution) => {
    setSelectedInstitution(institution);
    setInstitutionFormData({
      name: institution.name,
      type: institution.type,
      address: institution.address,
      region: institution.region,
      comuna: institution.comuna,
      phone: institution.phone,
      email: institution.email,
      rut: institution.rut || '',
      codigo_deis: institution.codigo_deis || '',
      capacity: institution.capacity || 0,
      specialties: institution.specialties || []
    });
    setInstitutionDialogOpen(true);
  };

  const handleDeleteUser = (userId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleDeleteInstitution = (institutionId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta institución?')) {
      deleteInstitutionMutation.mutate(institutionId);
    }
  };

  const handleUserFormSubmit = () => {
    if (selectedUser) {
      // Editing existing user
      const updateData: Partial<User> = {
        email: userFormData.email,
        phone: userFormData.phone,
        role: userFormData.role,
        first_name: userFormData.first_name,
        last_name: userFormData.last_name,
      };
      updateUserMutation.mutate({ userId: selectedUser.id, userData: updateData });
    } else {
      // Creating new user
      const createData: CreateUserRequest = {
        ...userFormData,
        date_of_birth: userFormData.date_of_birth || '1990-01-01', // Default if not provided
        national_id: userFormData.national_id || '12345678-9', // Default if not provided
      };
      createUserMutation.mutate(createData);
    }
  };

  const handleInstitutionFormSubmit = () => {
    if (selectedInstitution) {
      // Editing - would need update endpoint
      console.log('Editing institution not implemented yet');
    } else {
      // Creating new institution
      createInstitutionMutation.mutate(institutionFormData);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'doctor': return 'primary';
      case 'patient': return 'success';
      case 'family': return 'info';
      case 'hospital_staff': return 'warning';
      case 'ambulance_crew': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const tabs = [
    { label: 'Usuarios', icon: <PersonIcon /> },
    { label: 'Instituciones', icon: <HospitalIcon /> },
    { label: 'Estadísticas', icon: <AnalyticsIcon /> },
    { label: 'Configuración', icon: <SecurityIcon /> }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Panel de Administración
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gestión de usuarios, instituciones y configuración del sistema
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  {statsLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="h4">{stats?.users?.total || users.length}</Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Total Usuarios
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HospitalIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  {statsLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="h4">{stats?.institutions?.total || institutions.length}</Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Instituciones
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  {statsLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="h4">
                      {stats?.users?.doctors || users.filter(u => u.role === 'doctor').length}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Doctores Activos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  {statsLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="h4">{stats?.emergencies?.today || 24}</Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Emergencias Hoy
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab 
              key={index}
              icon={tab.icon} 
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Gestión de Usuarios</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateUser}
              disabled={createUserMutation.isPending}
            >
              Crear Usuario
            </Button>
          </Box>

          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : usersError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error al cargar usuarios. Mostrando datos de ejemplo.
            </Alert>
          ) : null}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>Institución</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {user.name || `${user.first_name} ${user.last_name}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role}
                        color={getRoleColor(user.role) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.institutionName || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.status || (user.is_active ? 'active' : 'inactive')}
                        color={getStatusColor(user.status || (user.is_active ? 'active' : 'inactive')) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditUser(user)}
                        disabled={updateUserMutation.isPending}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={deleteUserMutation.isPending}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {currentTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Gestión de Instituciones</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateInstitution}
              disabled={createInstitutionMutation.isPending}
            >
              Crear Institución
            </Button>
          </Box>

          {institutionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : null}

          <Grid container spacing={3}>
            {institutions.map((institution) => (
              <Grid item xs={12} md={6} key={institution.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">{institution.name}</Typography>
                      <Box>
                        <Chip 
                          label={institution.type}
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <IconButton 
                          size="small"
                          onClick={() => handleEditInstitution(institution)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteInstitution(institution.id)}
                          disabled={deleteInstitutionMutation.isPending}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {institution.address}
                    </Typography>
                    <Typography variant="body2">
                      📞 {institution.phone}
                    </Typography>
                    <Typography variant="body2">
                      📧 {institution.email}
                    </Typography>
                    <Typography variant="body2">
                      📍 {institution.region}, {institution.comuna}
                    </Typography>
                    {institution.capacity && (
                      <Typography variant="body2">
                        🏥 Capacidad: {institution.capacity} camas
                      </Typography>
                    )}
                    {institution.specialties && institution.specialties.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        {institution.specialties.map((specialty) => (
                          <Chip 
                            key={specialty}
                            label={specialty}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {currentTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Estadísticas del Sistema
          </Typography>
          <Alert severity="info">
            Panel de estadísticas en desarrollo - métricas de uso, tiempos de respuesta, etc.
          </Alert>
        </Paper>
      )}

      {currentTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Configuración del Sistema
          </Typography>
          <Alert severity="info">
            Panel de configuración en desarrollo - integraciones, APIs, notificaciones, etc.
          </Alert>
        </Paper>
      )}

      {/* User Creation/Edit Dialog */}
      <Dialog 
        open={userDialogOpen} 
        onClose={() => setUserDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Nombre"
                value={userFormData.first_name}
                onChange={(e) => setUserFormData(prev => ({ ...prev, first_name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Apellido"
                value={userFormData.last_name}
                onChange={(e) => setUserFormData(prev => ({ ...prev, last_name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={userFormData.phone}
                onChange={(e) => setUserFormData(prev => ({ ...prev, phone: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="RUT"
                value={userFormData.national_id}
                onChange={(e) => setUserFormData(prev => ({ ...prev, national_id: e.target.value }))}
                placeholder="12345678-9"
                required={!selectedUser}
              />
            </Grid>
            {!selectedUser && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={userFormData.role}
                  label="Rol"
                  onChange={(e) => setUserFormData(prev => ({ ...prev, role: e.target.value as User['role'] }))}
                >
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="doctor">Doctor</MenuItem>
                  <MenuItem value="patient">Paciente</MenuItem>
                  <MenuItem value="family">Familiar</MenuItem>
                  <MenuItem value="hospital_staff">Personal Hospital</MenuItem>
                  <MenuItem value="ambulance_crew">Tripulación Ambulancia</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Institución</InputLabel>
                <Select
                  value={userFormData.institution_id}
                  label="Institución"
                  onChange={(e) => setUserFormData(prev => ({ ...prev, institution_id: e.target.value }))}
                >
                  <MenuItem value="">Ninguna</MenuItem>
                  {institutions.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {(userFormData.role === 'doctor' || userFormData.role === 'hospital_staff') && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Especialización"
                    value={userFormData.specialization}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, specialization: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Número de Licencia"
                    value={userFormData.license_number}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, license_number: e.target.value }))}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Fecha de Nacimiento"
                type="date"
                value={userFormData.date_of_birth}
                onChange={(e) => setUserFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required={!selectedUser}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Género</InputLabel>
                <Select
                  value={userFormData.gender}
                  label="Género"
                  onChange={(e) => setUserFormData(prev => ({ ...prev, gender: e.target.value }))}
                >
                  <MenuItem value="">No especificar</MenuItem>
                  <MenuItem value="male">Masculino</MenuItem>
                  <MenuItem value="female">Femenino</MenuItem>
                  <MenuItem value="other">Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setUserDialogOpen(false);
              resetUserForm();
            }}
            disabled={createUserMutation.isPending || updateUserMutation.isPending}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained"
            onClick={handleUserFormSubmit}
            disabled={createUserMutation.isPending || updateUserMutation.isPending}
          >
            {createUserMutation.isPending || updateUserMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              selectedUser ? 'Actualizar' : 'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Institution Creation/Edit Dialog */}
      <Dialog 
        open={institutionDialogOpen} 
        onClose={() => setInstitutionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedInstitution ? 'Editar Institución' : 'Crear Nueva Institución'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de la Institución"
                value={institutionFormData.name}
                onChange={(e) => setInstitutionFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={institutionFormData.type}
                  label="Tipo"
                  onChange={(e) => setInstitutionFormData(prev => ({ ...prev, type: e.target.value as Institution['type'] }))}
                >
                  <MenuItem value="hospital">Hospital</MenuItem>
                  <MenuItem value="clinic">Clínica</MenuItem>
                  <MenuItem value="ambulance_service">Servicio de Ambulancia</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="RUT"
                value={institutionFormData.rut}
                onChange={(e) => setInstitutionFormData(prev => ({ ...prev, rut: e.target.value }))}
                placeholder="12345678-9"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección"
                value={institutionFormData.address}
                onChange={(e) => setInstitutionFormData(prev => ({ ...prev, address: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Región"
                value={institutionFormData.region}
                onChange={(e) => setInstitutionFormData(prev => ({ ...prev, region: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Comuna"
                value={institutionFormData.comuna}
                onChange={(e) => setInstitutionFormData(prev => ({ ...prev, comuna: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={institutionFormData.phone}
                onChange={(e) => setInstitutionFormData(prev => ({ ...prev, phone: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={institutionFormData.email}
                onChange={(e) => setInstitutionFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            {institutionFormData.type !== 'ambulance_service' && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Capacidad (camas)"
                  type="number"
                  value={institutionFormData.capacity}
                  onChange={(e) => setInstitutionFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) || 0 }))}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Código DEIS"
                value={institutionFormData.codigo_deis}
                onChange={(e) => setInstitutionFormData(prev => ({ ...prev, codigo_deis: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setInstitutionDialogOpen(false);
              resetInstitutionForm();
            }}
            disabled={createInstitutionMutation.isPending}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained"
            onClick={handleInstitutionFormSubmit}
            disabled={createInstitutionMutation.isPending}
          >
            {createInstitutionMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              selectedInstitution ? 'Actualizar' : 'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;